import {
  PresetStore,
  ResourceModel,
  ResourceModelConfig,
  SchedulerEventModel,
  SchedulerPro,
  ViewPreset,
  ViewPresetConfig,
} from '@bryntum/schedulerpro';

import { WbsSectionEntity } from '@/common/types/entities';
import {
  isDateInViewport,
  isResourceInViewport,
  isSomeDateWithinRangeInViewport,
} from '@/features/schedule/utils';
import { ScheduleViewStore } from '@/features/scheduleViews/scheduleViewStore';
import { getMainResourceId } from '@/services/store/schedule/parsers/base';
import { ScheduleStore } from '@/services/store/schedule/store';

import { useScheduleOperationsQueue } from '../composables/useScheduleOperationsQueue';

/**
 * Refreshes all the rows of the scheduler to update the UI in case any external data changed.
 * @param scheduler
 * @returns
 */
export function refreshScheduleUI(scheduler: SchedulerPro | undefined): void {
  if (!scheduler) return;

  if (!scheduler.features.lockRows || scheduler.features.lockRows.disabled) {
    scheduler.refreshRows();
  } else {
    scheduler.features.lockRows.subViews.forEach((subView) => {
      const subScheduler = subView as SchedulerPro;
      subScheduler.refreshRows();
    });
  }
}

/**
 * Refreshes the scheduler summary row to update the UI in case any external data changed.
 * @param scheduler
 * @returns
 */
export function refreshScheduleSummary(scheduler: SchedulerPro | undefined): void {
  if (!scheduler) return;

  if (scheduler.features.summary && !scheduler.features.summary.disabled) {
    scheduler.features.summary.refresh();
  }
}

/**
 * Refreshes the scheduler scroll position to prevent a desynced between the schedule and the summary row
 * @param scheduler
 * @returns
 */
export function refreshScrollPosition(scheduler: SchedulerPro | undefined): void {
  if (!scheduler) return;

  scheduler.scrollable.scrollTo(scheduler.scrollable.x + 1, scheduler.scrollable.y);
  scheduler.scrollable.scrollTo(scheduler.scrollable.x - 1, scheduler.scrollable.y);
}

/**
 * Expands a collapsed resource in the scheduler.
 * @param scheduler
 * @param resourceId
 * @returns
 */
export async function expandResource(
  scheduler: SchedulerPro | undefined,
  resourceId: string,
): Promise<void> {
  if (!scheduler) return;

  const resource = scheduler.resourceStore.getById(resourceId);

  if (!resource || resource.expanded) return;

  if (!scheduler.features.lockRows || scheduler.features.lockRows.disabled) {
    return scheduler.expand(resource);
  } else {
    return Promise.all(
      scheduler.features.lockRows.subViews.map((subView) => {
        const subScheduler = subView as SchedulerPro;
        return subScheduler.expand(resource);
      }),
    ).then(() => {});
  }
}

/**
 * Expands all ancestor sections of a given section to ensure it's visible.
 * @param ancestors - Array of ancestor sections to expand
 * @param scheduler - The scheduler instance
 * @param scheduleStore - The schedule store
 * @param scheduleViewStore - The schedule view store
 */
export async function expandAncestors(
  ancestors: WbsSectionEntity[],
  scheduler: SchedulerPro,
  scheduleStore: ScheduleStore,
  scheduleViewStore: ScheduleViewStore,
): Promise<void> {
  for (const ancestor of ancestors) {
    const resourceModel = scheduler.resourceStore.getById(ancestor.id);
    if (!resourceModel) {
      continue;
    }

    if (scheduleViewStore.collapsedRowIds.has(ancestor.id)) {
      await expandResource(scheduler, ancestor.id);
      scheduleViewStore.expandStateForRows([ancestor.id]);
      resourceModel.set({ expanded: true });
      scheduleStore.entities.resources.set(resourceModel.id.toString(), {
        ...scheduleStore.entities.resources.get(resourceModel.id.toString())!,
        expanded: true,
      });
    }
  }
}

/**
 * Collapses an expanded resource in the scheduler.
 * @param scheduler
 * @param resourceId
 * @returns
 */
export async function collapseResource(
  scheduler: SchedulerPro | undefined,
  resourceId: string,
): Promise<void> {
  if (!scheduler) return;

  const resource = scheduler.resourceStore.getById(resourceId) as ResourceModel;
  if (!resource.expanded) return;

  if (!scheduler.features.lockRows || scheduler.features.lockRows.disabled) {
    return scheduler.collapse(resource);
  } else {
    return Promise.all(
      scheduler.features.lockRows.subViews.map((subView) => {
        const subScheduler = subView as SchedulerPro;
        return subScheduler.collapse(resource);
      }),
    ).then(() => {});
  }
}

/**
 * Scrolls the schedule horizontally to the specified date, positioning the date either at the start, center or end of the viewport.
 * @param scheduler
 * @param dates
 * @param position
 * @param forceScroll
 * @returns
 */
export async function scrollToDate({
  scheduler,
  dates,
  position = 'center',
  forceScroll = false,
}: {
  scheduler: SchedulerPro | undefined;
  dates: Date[];
  position?: 'start' | 'center' | 'end';
  forceScroll?: boolean;
}): Promise<void> {
  if (!scheduler) return;
  if (dates.every((date) => isDateInViewport(scheduler, date)) && !forceScroll) return;
  return scheduler.scrollToDate(dates[0], { block: position });
}

/**
 * Scrolls the scheduler vertically to a specified resource, positioning the resource either at the start, center or end of the viewport.
 * @param scheduler
 * @param resourceId
 * @param position
 * @param forceScroll
 * @returns
 */
export async function scrollToResource({
  scheduler,
  resourceId,
  position = 'center',
  forceScroll = false,
}: {
  scheduler: SchedulerPro | undefined;
  resourceId: string;
  position?: 'start' | 'center' | 'end';
  forceScroll?: boolean;
}): Promise<void> {
  if (!scheduler) return;
  if (
    isResourceInViewport(
      scheduler,
      scheduler.resources.map((r: ResourceModel | ResourceModelConfig) => String(r.id ?? '')),
      resourceId,
    ) &&
    !forceScroll
  )
    return;

  if (!scheduler.features.lockRows || scheduler.features.lockRows.disabled) {
    const resource = scheduler.resourceStore.getById(resourceId) as ResourceModel;
    if (!resource) return;
    return scheduler.scrollRowIntoView(resource, { block: position });
  } else {
    return Promise.all(
      scheduler.features.lockRows.subViews.map((subView) => {
        const subScheduler = subView as SchedulerPro;
        const resource = subScheduler.resourceStore.getById(resourceId) as ResourceModel;
        if (!resource) return;
        return subView.scrollRowIntoView(resource, { block: position });
      }),
    ).then(() => {});
  }
}

/**
 * Scrolls the scheduler horizontally and vertically to a specified event, positioning the event either at the start, center or end of the viewport.
 * @param scheduler
 * @param eventId
 * @param position
 * @param forceScroll
 * @returns
 */
export async function scrollToEvent({
  scheduler,
  eventId,
  position = 'center',
}: {
  scheduler: SchedulerPro | undefined;
  eventId: string;
  position?: 'start' | 'center' | 'end';
}): Promise<void> {
  if (!scheduler) return;

  const event = scheduler.eventStore.getById(eventId) as SchedulerEventModel;
  if (!event) return;

  const resource = scheduler.resourceStore.getById(event.resourceId) as ResourceModel;
  if (!resource) return;

  const resourceIds = [
    getMainResourceId(),
    ...scheduler.resources.map((r: ResourceModel | ResourceModelConfig) => String(r.id ?? '')),
  ];
  const isResourceVisible = isResourceInViewport(scheduler, resourceIds, resource.id.toString());
  if (!isResourceVisible) {
    await scheduler.scrollRowIntoView(resource);
  }

  const isDateVisible = isSomeDateWithinRangeInViewport(scheduler, {
    startDate: new Date(event.startDate),
    endDate: new Date(event.endDate),
  });
  if (!isDateVisible) {
    return scheduler.scrollToDate(new Date(event.startDate), { block: position });
  }
}

/**
 * Returns the current view preset applied to the scheduler.
 * @param scheduler
 * @returns
 */
export function getCurrentViewPreset(scheduler: SchedulerPro | undefined): ViewPreset | undefined {
  return scheduler?.viewPreset as ViewPreset;
}

/**
 * Returns the current view preset applied to the scheduler.
 * @param scheduler
 * @returns
 */
export function registerViewPresets(
  scheduler: SchedulerPro | undefined,
  presets: ViewPresetConfig[],
): void {
  if (!scheduler) return;

  (scheduler.presets as PresetStore).removeAll();
  (scheduler.presets as PresetStore).add(presets);
}

/**
 * Zooms the scheduler to a specific view preset.
 * @param scheduler
 * @param presetId
 * @returns
 */
export function zoomToViewPreset(scheduler: SchedulerPro | undefined, presetId: string): void {
  if (!scheduler) return;
  const { queueFunction } = useScheduleOperationsQueue();

  queueFunction(() => {
    scheduler.zoomTo({ preset: presetId });
  });
}
